import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { observer, MobXProviderContext } from 'mobx-react';
import { Button, Form, Message } from 'semantic-ui-react';
import { DateInput } from 'semantic-ui-calendar-react';
import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';
import Modal from '../Modal';

import '../../css/ReactivateClassModal.less';

const ReactivateClassModal = observer((props) => {
  const { open, close, t, classroom } = props;
  const { timezoneStartDate: startDate, timezoneEndDate: endDate, ssoId } = classroom;
  const { classroomManager } = useContext(MobXProviderContext);
  const ModalBanner = useMemo(() => SatCoreComponent('ModalBanner'), []);
  const [value, setValue] = useState(null);
  const [error, setError] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Check if classroom is SSO
  const isSsoClassroom = useMemo(() => {
    return !!ssoId;
  }, [ssoId]);

  // Check if end date is past the current date
  const isEndDatePast = useMemo(() => {
    if (!endDate) return false;
    const endDateObj = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare dates only
    endDateObj.setHours(0, 0, 0, 0);
    return endDateObj < today;
  }, [endDate]);

  const validate = useCallback(() => {
    // Only validate end date if it's required (when end date is past)
    if (isEndDatePast) {
      if (value === null || value === '') {
        setError(t('endDateRequiredError', 'End Date is required!'));
        return false;
      }
      if (startDate !== null && startDate !== '') {
        const endRealDate = new Date(value);
        const startRealDate = new Date(startDate);
        if (endRealDate < startRealDate) {
          setError(t('endDateLessError', 'End date should be after start date'));
          return false;
        }
      }
    }
    return true;
  }, [isEndDatePast, startDate, value, t]);

  const submit = useCallback(async () => {
    setSubmitted(true);
    if (validate()) {
      // If end date is not past, use the existing endDate; otherwise use the new value
      const updatedEndDate = isEndDatePast ? value : endDate;
      const response = await classroomManager.updateClassroomObject({...classroom, archived: 'false', startDate, endDate: updatedEndDate});
      response ? close(null, null, true) : setError(t('generalError', 'Something has gone wrong.'));
    }
    setSubmitted(false);
  }, [validate, classroom, startDate, value, endDate, isEndDatePast, t]);

  return (
    <Modal
      className='reactivate-class-modal'
      closeOnDimmerClick={false}
      closeOnEscape={true}
      onClose={close}
      open={open}>
      <ModalBanner onClose={close} title={isEndDatePast ? t('title', 'Update Class End Date to Reactivate') : t('reactivateTitle', 'Reactivate Class')} />
      <Modal.Content>
        <p>
          {isEndDatePast 
            ? t('reactivateDescriptionPast', 'Reactivating this class will restore it to active status. Please update the end date to a future date to complete the reactivation.')
            : t('reactivateDescription', 'Reactivating this class will restore it to active status and make it available for use again.')}
        </p>
        {isSsoClassroom && (
          <p style={{ marginTop: '1em', fontStyle: 'italic' }}>
            {t(
              "ssoClassroomInfo",
              "Note: This classroom is managed by your district's rostering provider. Restoring it will prevent future updates and it will stay active until you manually archive it."
            )}
          </p>
        )}
        <Form>
          {isEndDatePast && (
            <Form.Field>
              <DateInput
                animation='false'
                dateFormat='MM/DD/YYYY'
                duration={0}
                iconPosition='right'
                label='End Date'
                name='endDate'
                onChange={(e, { value }) => setValue(value)}
                placeholder='End Date'
                value={value}
                />
            </Form.Field>
          )}
          {error && <Message content={error} error visible={true} />}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button basic onClick={close} primary>Cancel</Button>
        <Button primary disabled={submitted} onClick={submit}>Reactivate</Button>
      </Modal.Actions>
    </Modal>
  );
});

export default ReactivateClassModal;
SatCoreRegister('ReactivateClassModal', ReactivateClassModal);
